/**
 * Create the store with asynchronously loaded reducers
 */

import { createStore, applyMiddleware, compose } from 'redux';
import { fromJS } from 'immutable';
import { routerMiddleware } from 'react-router-redux';
import createSagaMiddleware from 'redux-saga';
import createReducer from './reducers';
import appSaga from './containers/App/sagas';
import dashboardSaga from './containers/Dashboard/sagas';
import growlerSaga from './containers/Notification/sagas';
import _ from 'lodash';
import request from 'vx-commons/lib/utils/request';

const sagaMiddleware = createSagaMiddleware();

const requirePluginSaga = (pluginsconfig) => (plugin) => new Promise((resolve) => {
  // if (!pluginsconfig.enable) return;
  if (pluginsconfig && pluginsconfig.data) {
    const customConfig = pluginsconfig.data.plugins.filter(value => value.name === plugin.name);
    if (customConfig[0] && !customConfig[0].enable) resolve(null);
  }

  require.ensure([], require => {
    const saga = require(`./plugins/${plugin.folder}/saga.js`);
    resolve(saga);
  });
});

export default async function configureStore(initialState = {}, history) {
  // Create the store with two middlewares
  // 1. sagaMiddleware: Makes redux-sagas work
  // 2. routerMiddleware: Syncs the location/URL path to the state
  const middlewares = [
    sagaMiddleware,
    routerMiddleware(history),
  ];

  const enhancers = [
    applyMiddleware(...middlewares),
  ];

  // New Redux chrome extension injection
  const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose; // eslint-disable-line no-trailing-spaces, no-underscore-dangle
  const store = createStore(
    createReducer(),
    fromJS(initialState),
    composeEnhancers(...enhancers)
  );

  // Extensions
  const packages = require.context('./plugins/', true, /[^/]+\/package\.json$/);
  let plugins = [];
  plugins = packages.keys().reduce((acc, path) => {
    const folder = /^\.\/([^/]+)/.exec(path)[1];
    const pkg = packages(path);

    // If name is node defined we do not like the package
    if ((pkg.name || '') === '') {
      console.error(`Plugin in ${folder} doesn't have name set in package.json`);
      return acc;
    }
    
    // Pick only selected properties and add some defaults
    return acc.concat(_.merge({
      folder,
      name: '',
      enable: true,
      description: '',
      version: '',
      state: 'not installed',
      dependencies: {},
    }, _.pick(pkg, [
      'name',
      'description',
      'version',
      'dependencies',
    ])));
  }, []);


  // Wait until all the plugin sagas are loaded
  await request('/release.json', { headers: { Accept: 'application/json', 'Content-Type': 'application/json' } })
    .then((release) => {
      if (!release.err) {
        console.log('Release', release);
        sessionStorage.setItem('release', JSON.stringify(release.data));
      } else {
        console.error('Cannot find a release file', release);
        sessionStorage.setItem('release', "{'err': 'not found'}");
      }

      // Return the Promise of the request call
      return request('/config.json', { headers: { Accept: 'application/json', 'Content-Type': 'application/json' } })
        .then((pluginsconfig) => {
          Promise.all(plugins.map(requirePluginSaga(pluginsconfig))).then((result) => {
            result.map((item) => item && sagaMiddleware.run(item.saga));
          });
        })
        .catch(() => {
          Promise.all(plugins.map(requirePluginSaga(null))).then((result) => {
            result.map((item) => item && sagaMiddleware.run(item.saga));
          });
        });
    });

  sagaMiddleware.run(appSaga);
  sagaMiddleware.run(dashboardSaga);
  sagaMiddleware.run(growlerSaga);

  store.runSaga = sagaMiddleware.run;
  store.asyncReducers = {}; // Async reducer registry

  // Make reducers hot reloadable, see http://mxs.is/googmo
  /* istanbul ignore next */
  if (module.hot) {
    module.hot.accept('./reducers', () => {
      import('./reducers').then((reducerModule) => {
        const createReducers = reducerModule.default;
        const nextReducers = createReducers(store.asyncReducers);
        store.replaceReducer(nextReducers);
      });
    });
  }

  return store;
}



// WEBPACK FOOTER //
// ./src/store.js